import * as React from 'react';

import classnames from 'classnames';
import * as ramda from 'ramda';
import _ from 'underscore';

import {bucketSchemaFields, getSortedBucketKeys} from 'nutshell-core/bucket-schema-fields';
import * as Schema from 'nutshell-core/schema';
import type {FilterObject, EntityListType} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
import {SearchBar} from 'shells/search-bar/search-bar';

import {FilterBucket} from './filter-bucket';

import styles from './filter-drawer.module.css';

type Props = {
    allowNegation: boolean;
    fields: Schema.FieldProperties[];
    activeFilters: FilterObject[];
    primaryFilters?: string[];
    entityType: EntityListType;
    isOpen: boolean;
    placeholder?: string;
    hasTopBorder?: boolean;
    hasSearch?: boolean;
    onSubmitFilter: (filterObj: FilterObject, field: Schema.FieldProperties) => void;
    onRemoveFilter: (filterKey: string) => void;
    shownColumns?: string[];
    updateShownColumns?: (arg1: string[], arg2: boolean) => void;
    shouldShowPreappliedFiltersAtTop?: boolean;
};

type State = {
    preappliedFilters: string[];
    openFilters: string[];
    searchString: string;
    shouldShowSecondaryFilters: boolean;
    shouldAutoFocusSearch: boolean;
};

/**
 * This is a filtering UI element which displays a list of the various filters
 * that can be applied for a given entity type, with each filter able to be opened,
 * configured, and applied.
 *
 * @param {Object[]} fields             - Array of schema fields
 * @param {string[]} [primaryFilters]   - Optional list of filter names to always display,
 *                                        any others will be hidden behind a "show all" link
 * @param {Object[]} activeFilters      - List of actively-applied filter objects
 * @param {string} entityType           - The type of entity being filtered ('leads', 'contacts', or 'accounts')
 * @param {boolean} isOpen              - True if the drawer is open.  Used to determine "pre-applied" filters
 * @param {Function} onSubmitFilter     - Callback to apply a filter
 * @param {Function} onRemoveFilter     - Callback to remove a filter without closing the section
 * @return {Object}                     - React component for group of filters
 */
export class FilterDrawer extends React.PureComponent<Props, State> {
    static defaultProps = {
        placeholder: 'Search filters…',
        allowNegation: true,
        hasSearch: true,
        shouldShowPreappliedFiltersAtTop: true,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            // @ts-expect-error
            preappliedFilters: props.activeFilters.map((filterObj) => Object.keys(filterObj)[0]),
            // @ts-expect-error
            openFilters: props.activeFilters.map((filterObj) => Object.keys(filterObj)[0]),
            searchString: '',
            shouldShowSecondaryFilters: false,
            shouldAutoFocusSearch: false,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Check if the map filter has been added, in which case we need to
        // treat it as "preapplied" so it can break out of secondary section.
        // Otherwise, it's really confusing to have an applied filter you can't
        // find.
        if (
            ramda.any((filter) => isMap(filter))(nextProps.activeFilters) &&
            !ramda.any((filter) => isMap(filter))(this.props.activeFilters)
        ) {
            this.setState((state) => ({
                // If `map` filter is active, always treat it as "pre-applied",
                // because it is applied automatically on map view, and needs to go to top
                preappliedFilters: [...state.preappliedFilters, 'map'],
            }));
        }

        // If the map filter has been _removed_, we should clear it out of the
        // list of preapplied filters, so a confusing filter option isn't sitting at
        // the top of the filter drawer when viewing the list/board/reports.
        if (
            ramda.any((filter) => isMap(filter))(this.props.activeFilters) &&
            !ramda.any((filter) => isMap(filter))(nextProps.activeFilters)
        ) {
            this.setState((state) => ({
                preappliedFilters: state.preappliedFilters.filter((filterKey) => {
                    return filterKey !== 'map';
                }),
            }));
        }

        // Filters can be modified from outside the drawer, respond to these changes.
        if (!ramda.equals(nextProps.activeFilters, this.props.activeFilters)) {
            this.setState({
                // @ts-expect-error
                openFilters: nextProps.activeFilters.map((filterObj) => Object.keys(filterObj)[0]),
            });
        }

        if (nextProps.isOpen && !this.props.isOpen) {
            this.setState({
                // @ts-expect-error
                preappliedFilters: nextProps.activeFilters.map(
                    (filterObj) => Object.keys(filterObj)[0]
                ),
                shouldAutoFocusSearch: true,
            });
        }
    }

    render() {
        const searchedFields = this.props.fields.filter((field) => {
            // When the funnel report period filter is applied, for some reason
            // the first `field` we get here is `undefined`, so we have to add a little safety
            return (
                field &&
                (field.title.toLowerCase().includes(this.state.searchString.toLowerCase()) ||
                    (field.bucket &&
                        field.bucket.toLowerCase().includes(this.state.searchString.toLowerCase())))
            );
        });

        const preAppliedFields = searchedFields.filter((field) =>
            this.state.preappliedFilters.includes(field.name)
        );
        const nonPreappliedFields = searchedFields.filter(
            (field) => !this.state.preappliedFilters.includes(field.name)
        );
        const bucketedFields = this.props.shouldShowPreappliedFiltersAtTop
            ? bucketSchemaFields(nonPreappliedFields)
            : bucketSchemaFields([...preAppliedFields, ...nonPreappliedFields]);
        const styleNames = classnames({
            [styles.container]: this.props.isOpen,
            [styles['container--closed']]: !this.props.isOpen,
            [styles['container--borderTop']]: this.props.hasTopBorder,
        });

        if (this.props.isOpen) {
            return (
                // id is used in tours, do not change without updating tour.ts
                <div className={styleNames} id='filter-drawer'>
                    {this.props.hasSearch && (
                        <div className={styles.search}>
                            <SearchBar
                                value={this.state.searchString}
                                placeholder={this.props.placeholder}
                                onChange={this.handleSearchChange}
                                isClearable={true}
                                hasBorder={true}
                                autoFocus={this.state.shouldAutoFocusSearch}
                                styleOptions={{height: 32, borderColor: colors.offWhiteDk}}
                            />
                        </div>
                    )}
                    {this.props.shouldShowPreappliedFiltersAtTop && preAppliedFields.length > 0 && (
                        <FilterBucket
                            {...this.props}
                            fields={preAppliedFields}
                            shouldShowSecondaryFilters={true}
                            openFilters={this.state.openFilters}
                            onOpenSection={this.handleSectionOpen}
                            onCloseSection={this.handleSectionClose}
                            shownColumns={this.props.shownColumns}
                            updateShownColumns={this.props.updateShownColumns}
                        />
                    )}
                    {getSortedBucketKeys(bucketedFields).map((bucketTitle) => {
                        return (
                            <FilterBucket
                                {...this.props}
                                key={bucketTitle}
                                title={bucketTitle}
                                fields={bucketedFields[bucketTitle]}
                                shouldShowSecondaryFilters={this.state.shouldShowSecondaryFilters}
                                onShowAllClick={this.handleShowSecondaryFilters}
                                openFilters={this.state.openFilters}
                                onOpenSection={this.handleSectionOpen}
                                onCloseSection={this.handleSectionClose}
                                shownColumns={this.props.shownColumns}
                                updateShownColumns={this.props.updateShownColumns}
                            />
                        );
                    })}
                </div>
            );
        }

        return <></>;
    }

    handleSectionOpen = (filterKey: string) => {
        // @ts-expect-error
        if (typeof window.trackJs !== 'undefined')
            // @ts-expect-error
            window.trackJs.console.info('User opened filter section:', filterKey);
        this.setState(openFilterSection(filterKey));
    };

    handleSectionClose = (filterKey: string) => {
        this.props.onRemoveFilter(filterKey);
        // @ts-expect-error
        if (typeof window.trackJs !== 'undefined')
            // @ts-expect-error
            window.trackJs.console.info('User closed filter section:', filterKey);
        this.setState(closeFilterSection(filterKey));
    };

    handleSearchChange = (search: string) => {
        this.setState({
            searchString: search,
            shouldShowSecondaryFilters: true,
        });
    };

    handleShowSecondaryFilters = () => {
        this.setState({
            shouldShowSecondaryFilters: true,
        });
    };
}

function openFilterSection(filterKey: string) {
    // @ts-expect-error
    return (state) => ({
        openFilters: [...state.openFilters, filterKey],
    });
}

function closeFilterSection(filterKey: string) {
    // @ts-expect-error
    return (state) => ({
        openFilters: _.without(state.openFilters, filterKey),
    });
}

/*
Helper function to determine whether a filter object is a `map` filter
 */
const isMap = ramda.has('map');
